import { VariantUpdatedEvent, VariantSelectedEvent, ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/utilities';
// import { morph } from '@theme/morph';

class VariantPicker extends HTMLElement {
  // Contrôleur pour annuler les requêtes en cours
  #abortController;

  #handleSubscriptionUpdated = () => {
    this.#updateUnitPrices();
  };

  /**
   * Portée du formulaire produit dans une réponse HTML (évite de prendre un variant-picker ailleurs sur la page).
   */
  #productFormScope(doc) {
    return (
      doc.querySelector('.product-section product-form[data-ref="product-form"]') ||
      doc.querySelector('.product-section product-form') ||
      doc.querySelector('[data-ref="product-section-content"] product-form') ||
      doc.querySelector('product-form[data-ref="product-form"]') ||
      doc.querySelector('product-form')
    );
  }

  /**
   * Cible le même <variant-picker> que cette instance (id Shopify du bloc) dans une réponse HTML.
   * Priorité au formulaire produit pour éviter un picker d’une autre section / recommandation.
   */
  #resolveVariantPickerInDocument(doc) {
    if (!doc?.querySelector) return null;

    const tag = this.tagName.toLowerCase();
    const formScope = this.#productFormScope(doc);

    if (formScope) {
      if (this.id) {
        const scopedById = formScope.querySelector(`${tag}#${CSS.escape(this.id)}`);
        if (scopedById) return scopedById;
      }
      const inForm = formScope.querySelector(tag);
      if (inForm) return inForm;
    }

    if (this.id) {
      const byId = doc.getElementById(this.id);
      if (byId?.tagName?.toLowerCase() === tag) return byId;
    }

    return doc.querySelector(tag);
  }

  connectedCallback() {
    this.addEventListener('change', this.variantChanged.bind(this));
    document.addEventListener(ThemeEvents.subscriptionUpdated, this.#handleSubscriptionUpdated);
    this.#ensureVisibleVariantSelected();
    this.#updateUnitPrices();
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.subscriptionUpdated, this.#handleSubscriptionUpdated);
  }

  /**
   * Recalcule les prix par kg/L selon l'option achat unique ou abonnement (aligné thème live Reglo).
   */
  #updateUnitPrices() {
    if (this.dataset.showUnitPrice !== 'true') return;

    const productForm =
      this.closest('product-form') ||
      this.closest('[data-ref="product-form"]') ||
      this.#productFormScope(document);

    const subscription = productForm?.querySelector('subscription-component');
    const useSubscription =
      subscription?.isSubscriptionActivated?.() && subscription?.isSubscriptionSelected?.();

    const defaultUnit = this.dataset.unitPriceUnit || 'kg';

    for (const unitPriceEl of this.querySelectorAll('.variant-picker__unit-price')) {
      const size = parseFloat(unitPriceEl.dataset.size);
      let priceCents = parseInt(unitPriceEl.dataset.priceCents, 10);
      const unit = unitPriceEl.dataset.pricePerUnit || defaultUnit;

      if (!size || !priceCents) continue;

      if (useSubscription && subscription) {
        priceCents = subscription.calculateAdjustedPrice(priceCents, 1);
      }

      const pricePerCents = Math.round(priceCents / size);
      unitPriceEl.textContent = `${formatMoney(pricePerCents)}/${unit}`;
    }
  }

  #ensureVisibleVariantSelected() {
    const hiddenVariantInput = this.querySelector('input[name="id"]');
    const selectedVariantId = hiddenVariantInput instanceof HTMLInputElement ? hiddenVariantInput.value : '';

    for (const fieldset of this.querySelectorAll('fieldset.variant-picker__option')) {
      if (fieldset.querySelector('input:checked')) continue;

      const matchingInput =
        selectedVariantId &&
        fieldset.querySelector(
          `.variant-picker__option-value input[data-variant-id="${selectedVariantId}"]:not([aria-disabled="true"])`,
        );

      const firstVisibleInput =
        matchingInput ||
        fieldset.querySelector('.variant-picker__option-value input:not([aria-disabled="true"])');

      if (firstVisibleInput instanceof HTMLInputElement) {
        firstVisibleInput.checked = true;
      }
    }

    this.#fillEmptyVariantId();
  }

  /**
   * Une variante sans image dédiée reste achetable. Si l'input name="id" est vide,
   * /cart/add répond 422 "Cannot find variant". On reprend l'option cochée.
   */
  #fillEmptyVariantId() {
    const hiddenVariantInput = this.querySelector('input[name="id"]');
    if (!(hiddenVariantInput instanceof HTMLInputElement) || hiddenVariantInput.value) return;

    const checkedOption = this.querySelector('[data-variant-id]:checked');
    if (!checkedOption?.dataset.variantId) return;

    hiddenVariantInput.value = checkedOption.dataset.variantId;
  }

  /**
   * Gère l'événement de changement de variante.
   * @param {Event} event - L'événement de changement de variante.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Met à jour l'option sélectionnée dans l'interface
    this.updateSelectedOption(event.target);

    // Déclenche un événement personnalisé pour notifier la sélection
    this.dispatchEvent(new VariantSelectedEvent({ id: event.target.dataset.optionValueId ?? '' }));

    // Lance la requête pour mettre à jour la section
    this.fetchUpdatedSection(this.buildRequestUrl());

    // Met à jour l'URL avec l'ID de variante sur les pages produit
    this.updateUrl(event.target);
  }

  // Met à jour l'option sélectionnée dans l'interface.
  updateSelectedOption(target) {
    // Si c'est une chaîne, trouve l'élément correspondant pour récupérer l'ID de la valeur d'option
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    // Met à jour l'état "checked" pour les boutons radio
    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    // Met à jour l'attribut "selected" pour les éléments select
    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      // Supprime l'attribut selected de toutes les options
      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      // Ajoute l'attribut selected à la nouvelle option
      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  // Construit l'URL de requête pour récupérer les données de variante.
  buildRequestUrl() {
    let productUrl = this.dataset.productUrl;

    // On utilise la nouvelle API de Shopify pour récupérer les données de variante avec option_values.
    const params = [];
    params.push(`option_values=${this.selectedOptionsValues.join(',')}`);

    // Si variant-picker est un enfant de quick-add-component ou swatches-variant-picker-component,
    // nous devons ajouter section_id=section-rendering-product-card à l'URL
    // if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
    //   if (productUrl?.includes('?')) {
    //     productUrl = productUrl.split('?')[0];
    //   }
    //   return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    // }
    return `${productUrl}?${params.join('&')}`;
  }

  // Récupère la section mise à jour depuis le serveur.
  fetchUpdatedSection(requestUrl) {
    // Annule la requête fetch précédente si elle est encore en cours
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');

        const pickerInScope = this.#resolveVariantPickerInDocument(html);

        // Récupère les données JSON de la variante depuis la réponse
        const textContent = pickerInScope?.querySelector('script[type="application/json"]')?.textContent;
        if (!textContent) return;

        // Met à jour seulement le variant picker
        this.updateVariantPicker(html);

        // Déclenche un événement pour notifier la mise à jour de la variante
        if (this.selectedOptionId) {
          this.dispatchEvent(
            new VariantUpdatedEvent(JSON.parse(textContent), this.selectedOptionId, {
              html,
              productId: this.dataset.productId ?? '',
            }),
          );
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.log("Fetch interrompu par l'utilisateur");
        } else {
          console.error(error);
        }
      });
  }

  // Re-rend le sélecteur de variante avec les nouvelles données.
  updateVariantPicker(newHtml) {
    const newVariantPickerSource = this.#resolveVariantPickerInDocument(newHtml);

    if (!newVariantPickerSource) {
      throw new Error('Aucune nouvelle source de variant picker trouvée');
    }

    this.#preserveOptionDetails(newVariantPickerSource);

    /**
     * Importer les nœuds depuis le document parsé au lieu de this.innerHTML = string.
     * Le round-trip innerHTML peut faire perdre ou altérer certains blocs (badges, prix unitaire).
     */
    const imported = document.importNode(newVariantPickerSource, true);
    const nodes = Array.from(imported.childNodes);
    this.replaceChildren(...nodes);
    this.#ensureVisibleVariantSelected();
    this.#updateUnitPrices();
  }

  /**
   * Le endpoint ?option_values met parfois à jour les labels/états mais omet des éléments
   * décoratifs déjà présents dans le DOM (badge, prix unitaire par option).
   * On les conserve depuis le picker courant tant qu'ils manquent dans la réponse.
   */
  #preserveOptionDetails(newVariantPickerSource) {
    const showUnitPrice = this.dataset.showUnitPrice === 'true';
    const showBadges = this.dataset.showBadges === 'true';
    const showOptionName = this.dataset.showOptionName === 'true';

    if (!showOptionName) {
      newVariantPickerSource.querySelectorAll('.variant-picker__option-name').forEach((node) => node.remove());
    }

    const currentOptionsByValueId = new Map();

    for (const currentOptionValue of this.querySelectorAll('.variant-picker__option-value')) {
      const currentInput = currentOptionValue.querySelector('[data-option-value-id]');
      const currentValueId = currentInput?.dataset?.optionValueId;
      if (!currentValueId) continue;
      currentOptionsByValueId.set(currentValueId, currentOptionValue);
    }

    for (const nextOptionValue of newVariantPickerSource.querySelectorAll('.variant-picker__option-value')) {
      const nextInput = nextOptionValue.querySelector('[data-option-value-id]');
      const nextValueId = nextInput?.dataset?.optionValueId;
      if (!nextValueId) continue;

      const currentOptionValue = currentOptionsByValueId.get(nextValueId);
      if (!currentOptionValue) continue;

      const nextLabel = nextOptionValue.querySelector('label');
      const currentLabel = currentOptionValue.querySelector('label');

      if (!showUnitPrice) {
        nextLabel?.querySelectorAll('.variant-picker__unit-price').forEach((node) => node.remove());
      } else {
        const nextUnitPrice = nextLabel?.querySelector('.variant-picker__unit-price');
        const currentUnitPrice = currentLabel?.querySelector('.variant-picker__unit-price');
        if (!nextUnitPrice && currentUnitPrice && nextLabel) {
          nextLabel.appendChild(currentUnitPrice.cloneNode(true));
        }
      }

      const nextImageWrap = nextLabel?.querySelector('.variant-picker__image-wrap');
      const currentImageWrap = currentLabel?.querySelector('.variant-picker__image-wrap');
      if (!nextImageWrap && currentImageWrap && nextLabel) {
        nextLabel.insertBefore(currentImageWrap.cloneNode(true), nextLabel.firstChild);
      }

      const nextDescription = nextLabel?.querySelector('.variant-picker__description');
      const currentDescription = currentLabel?.querySelector('.variant-picker__description');
      if (!nextDescription && currentDescription && nextLabel) {
        const unitPrice = nextLabel.querySelector('.variant-picker__unit-price');
        if (unitPrice) {
          nextLabel.insertBefore(currentDescription.cloneNode(true), unitPrice);
        } else {
          const labelNode = nextLabel.querySelector('.variant-picker__option-label, span.variant-picker__label-text');
          if (labelNode?.nextSibling) {
            nextLabel.insertBefore(currentDescription.cloneNode(true), labelNode.nextSibling);
          } else {
            nextLabel.appendChild(currentDescription.cloneNode(true));
          }
        }
      }

      const nextCustomTitle = nextLabel?.querySelector('.variant-picker__option-label');
      const currentCustomTitle = currentLabel?.querySelector('.variant-picker__option-label');
      const nextPlainTitle = nextLabel?.querySelector('span.variant-picker__label-text');
      if (!nextCustomTitle && currentCustomTitle && nextLabel && nextPlainTitle) {
        nextPlainTitle.replaceWith(currentCustomTitle.cloneNode(true));
      }

      if (!showBadges) {
        nextOptionValue.querySelectorAll('.variant-picker__badge').forEach((node) => node.remove());
      } else {
        const badgePositions = ['top', 'bottom'];
        for (const position of badgePositions) {
          const nextBadge = nextOptionValue.querySelector(`.variant-picker__badge--${position}`);
          const currentBadge = currentOptionValue.querySelector(`.variant-picker__badge--${position}`);
          if (nextBadge || !currentBadge) continue;

          const badgeClone = currentBadge.cloneNode(true);
          if (position === 'top') {
            nextOptionValue.prepend(badgeClone);
          } else {
            nextOptionValue.appendChild(badgeClone);
          }
        }
      }

      if (
        !nextOptionValue.classList.contains('variant-picker__option-value--no-overflow') &&
        currentOptionValue.classList.contains('variant-picker__option-value--no-overflow')
      ) {
        nextOptionValue.classList.add('variant-picker__option-value--no-overflow');
      }
    }
  }

  // Met à jour l'URL avec l'ID de variante sur les pages produit
  updateUrl(target) {
    if (!(target instanceof HTMLElement)) return;

    // Détermine si nous sont sur une page produit (pas dans une carte produit ou dialogue)
    const isOnProductPage =
      Theme.template.name === 'product' &&
      !target.closest('product-card') &&
      !target.closest('quick-add-dialog');

    // Prépare l'URL pour la mise à jour de l'historique du navigateur
    const url = new URL(window.location.href);

    let variantId;

    // Extrait l'ID de la variante selon le type d'élément (radio ou select)
    if (target instanceof HTMLInputElement && target.type === 'radio') {
      variantId = target.dataset.variantId || null;
    } else if (target instanceof HTMLSelectElement) {
      const selectedOption = target.options[target.selectedIndex];
      variantId = selectedOption?.dataset.variantId || null;
    }

    // Met à jour l'URL avec l'ID de variante sur les pages produit
    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Met à jour l'URL dans l'historique du navigateur si elle a changé
    if (url.href !== window.location.href) {
      history.replaceState({}, '', url.toString());
    }
  }

  // Récupère l'option actuellement sélectionnée.
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  // Récupère l'ID de l'option actuellement sélectionnée.
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error("Aucun ID de valeur d'option trouvé");
    }

    return optionValueId;
  }

  // Récupère toutes les valeurs d'options actuellement sélectionnées.
  get selectedOptionsValues() {
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error("Aucun ID de valeur d'option trouvé");

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
